import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiError } from 'src/api/axiosClient/types';
import { RootState } from 'src/store';
import { CampaignListItem, GetCampaignsParams, JoinCampaignOffer } from '../types';
import { CampaignsState } from './types';

const initialState: CampaignsState = {
  loading: {
    getList: false,
    getAwayMessageCampaigns: false,
    activate: false,
    deactivate: false,
    createAwayMessageCampaign: false,
    updateAwayMessageCampaign: false,
    start: false,
  },
  filter: {
    companyId: '',
    status: 'all',
    offset: 0,
    limit: 25,
    page: 0,
    configId: 'all',
  },
  list: null,
  awayCustomMessageCampaign: undefined,
  awayRecurringMessagesCampaign: undefined,
  joinCampaignOffers: [],
  error: null,
};

const campaignsSlice = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {
    setLoading: (
      state,
      action: PayloadAction<Partial<Record<keyof CampaignsState['loading'], boolean>>>,
    ) => {
      state.loading = { ...state.loading, ...action.payload };
    },
    setFilter(state, action: PayloadAction<Partial<GetCampaignsParams>>) {
      state.filter = {
        ...state.filter,
        ...action.payload,
      };
    },
    setList: (
      state,
      action: PayloadAction<{
        items: CampaignListItem[];
        count: number;
      }>,
    ) => {
      state.list = action.payload;
    },
    setAwayCustomMessageCampaign: (
      state,
      action: PayloadAction<{
        item: CampaignListItem | undefined;
      }>,
    ) => {
      state.awayCustomMessageCampaign = action.payload.item;
    },
    setAwayRecurringMessagesCampaign: (
      state,
      action: PayloadAction<{
        item: CampaignListItem | undefined;
      }>,
    ) => {
      state.awayRecurringMessagesCampaign = action.payload.item;
    },
    queueJoinCampaignOffer: (state, action: PayloadAction<JoinCampaignOffer>) => {
      state.joinCampaignOffers.push(action.payload);
    },
    dismissJoinCampaignOffer: state => {
      state.joinCampaignOffers.shift();
    },
    setError: (state, action: PayloadAction<ApiError>) => {
      state.error = action.payload;
    },
    resetList: state => {
      state.filter = initialState.filter;
      state.list = initialState.list;
    },
    resetError: state => {
      state.error = initialState.error;
    },
    resetSlice: () => {
      initialState;
    },
  },
});

export const isCampaignsLoading = (state: RootState) => state.campaigns.loading;

export const getCampaigns = (state: RootState) => state.campaigns.list;

export const getAwayCustomMessageCampaign = (state: RootState) =>
  state.campaigns.awayCustomMessageCampaign;

export const getAwayRecurringMessagesCampaign = (state: RootState) =>
  state.campaigns.awayRecurringMessagesCampaign;

// Offers queue up, so one arriving while another is open waits for its turn.
export const getJoinCampaignOffer = (state: RootState) =>
  state.campaigns.joinCampaignOffers[0] ?? null;

export const isCampaignsError = (state: RootState) => state.campaigns.error;

export const {
  setLoading,
  setFilter,
  setList,
  setAwayCustomMessageCampaign,
  setAwayRecurringMessagesCampaign,
  queueJoinCampaignOffer,
  dismissJoinCampaignOffer,
  setError,
  resetList,
  resetError,
  resetSlice,
} = campaignsSlice.actions;

export const campaignsReducer = campaignsSlice.reducer;
